import { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ImageUpload Component
 * 
 * Reusable component for uploading images.
 * 
 * Props:
 * - value: Current image URL (string)
 * - onChange: Callback when image is changed/uploaded (function(url))
 * - placeholder: Text to display when no image is selected (string)
 * - className: Additional CSS classes (string)
 */
export default function ImageUpload({ value, onChange, placeholder = "Subir imagen", className = "" }) {
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast.error('Por favor, sube un archivo de imagen válido');
            return;
        }

        // Validate file size (e.g., max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('La imagen no debe superar los 5MB');
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            // Get token from localStorage (assuming it's stored there by AuthContext)
            const token = localStorage.getItem('token');

            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error('Error en la subida');
            }

            const data = await response.json();
            const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;

            onChange(imageUrl);
            toast.success('Imagen subida correctamente');
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error('Error al subir la imagen');
        } finally {
            setUploading(false);
            // Reset input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const clearImage = () => {
        onChange('');
    };

    return (
        <div className={`space-y-3 w-full min-w-0 ${className}`}>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
            />

            {value ? (
                <div className="relative group rounded-lg overflow-hidden border border-border bg-muted/20">
                    <div className="w-full h-28 relative">
                        <img
                            src={value}
                            alt="Preview"
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.src = 'https://placehold.co/600x400?text=Error+Loading+Image'; }}
                        />
                    </div>

                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <Upload className="w-3 h-3 mr-2" />
                            Cambiar
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={clearImage}
                        >
                            <X className="w-3 h-3 mr-2" />
                            Quitar
                        </Button>
                    </div>
                </div>
            ) : (
                <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-muted-foreground/25 hover:border-primary/50 hover:bg-primary/5 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer transition-colors h-32"
                >
                    {uploading ? (
                        <>
                            <Loader2 className="w-8 h-8 text-primary animate-spin mb-2" />
                            <p className="text-sm text-muted-foreground">Subiendo...</p>
                        </>
                    ) : (
                        <>
                            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-2">
                                <ImageIcon className="w-5 h-5 text-muted-foreground" />
                            </div>
                            <p className="text-sm font-medium text-foreground">{placeholder}</p>
                            <p className="text-xs text-muted-foreground mt-1">Click para seleccionar</p>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
